import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Container,
  Paper,
  Grid,
  InputAdornment,
  Fade,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoaderWrapper } from './index';
import { LocationAPI } from '../services/locationApi';

// Validation schema
const schema = yup.object({
  latitude: yup
    .number()
    .required('Latitude is required')
    .min(-90, 'Latitude must be between -90 and 90 degrees')
    .max(90, 'Latitude must be between -90 and 90 degrees'),
  longitude: yup
    .number()
    .required('Longitude is required')
    .min(-180, 'Longitude must be between -180 and 180 degrees')
    .max(180, 'Longitude must be between -180 and 180 degrees'),
});

const LocationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [coordinatesValid, setCoordinatesValid] = useState(null);
  const [error, setError] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  
  // Ref to track last validated coordinates to prevent duplicate API calls
  const lastValidatedRef = useRef({ lat: null, lng: null });

  // Get user info from localStorage
  const orgId = localStorage.getItem('orgId');
  const appId = localStorage.getItem('appId');

  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      latitude: '',
      longitude: '',
    }
  });

  const watchedCoordinates = watch(['latitude', 'longitude']);

  useEffect(() => {
    // Check if user is authenticated
    if (!orgId) {
      toast.error('Please login first');
      navigate('/login');
      return;
    }
  }, [orgId, navigate]);

  // Debounced validation effect
  useEffect(() => {
    // Auto-validate coordinates when they change (with debouncing)
    const [lat, lng] = watchedCoordinates;
    if (lat && lng && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng))) {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);
      
      // Check if these coordinates have already been validated
      if (lastValidatedRef.current.lat === latNum && lastValidatedRef.current.lng === lngNum) {
        return; // Skip validation if coordinates haven't changed
      }
      
      // Debounce the validation to avoid rapid API calls
      const timer = setTimeout(() => {
        validateCoordinates(latNum, lngNum);
      }, 500); // Wait 500ms after user stops typing
      
      return () => clearTimeout(timer); // Cleanup timer on re-render
    } else {
      setCoordinatesValid(null);
      lastValidatedRef.current = { lat: null, lng: null };
    }
  }, [watchedCoordinates]);

  // Validate coordinates against India bounds
  const validateCoordinates = async (lat, lng) => {
    if (validating) return;
    
    // Update the last validated coordinates
    lastValidatedRef.current = { lat, lng };
    
    setValidating(true);
    try {
      const data = await LocationAPI.validateCoordinates(lat, lng);
      
      if (data.success) {
        setCoordinatesValid(data.valid);
        if (!data.valid) {
          setError(data.message || 'Coordinates are not within India');
        } else {
          setError('');
        }
      } else {
        setCoordinatesValid(false);
        setError(data.message || 'Invalid coordinates');
      }
    } catch (error) {
      console.error('Error validating coordinates:', error);
      setCoordinatesValid(false);
      setError('Failed to validate coordinates');
    } finally {
      setValidating(false);
    }
  };

  // Get current location using geolocation API
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }

    setGettingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const accuracy = position.coords.accuracy;
        
        console.log('Geolocation result:', {
          latitude: lat,
          longitude: lng,
          accuracy: accuracy,
          timestamp: new Date(position.timestamp).toLocaleString()
        });
        
        // Format coordinates to 6 decimal places for better precision
        const formattedLat = lat.toFixed(6);
        const formattedLng = lng.toFixed(6);
        
        // Set both values together using batch update to avoid multiple re-renders
        setValue('latitude', formattedLat, { shouldValidate: false });
        setValue('longitude', formattedLng, { shouldValidate: false });
        
        // Show success message with accuracy info
        const accuracyText = accuracy < 100 ? 'high accuracy' : accuracy < 500 ? 'medium accuracy' : 'low accuracy';
        toast.success(`Location detected successfully (${accuracyText}: ±${Math.round(accuracy)}m)`);
        
        // Also log the DMS format for comparison (for debugging)
        const latDMS = convertToDMS(lat, 'lat');
        const lngDMS = convertToDMS(lng, 'lng');
        console.log('Location in DMS format:', `${latDMS} ${lngDMS}`);
        
        setGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        let errorMessage = 'Failed to get current location';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user. Please allow location access and try again.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please try again or enter coordinates manually.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage = 'Unknown error getting location. Please try again.';
            break;
        }
        
        toast.error(errorMessage);
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0 // Don't use cached position - get fresh location
      }
    );
  };

  // Helper function to convert decimal degrees to DMS format (for debugging)
  const convertToDMS = (coordinate, type) => {
    const absolute = Math.abs(coordinate);
    const degrees = Math.floor(absolute);
    const minutesNotTruncated = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesNotTruncated);
    const seconds = Math.floor((minutesNotTruncated - minutes) * 60);
    
    let direction = '';
    if (type === 'lat') {
      direction = coordinate >= 0 ? 'N' : 'S';
    } else {
      direction = coordinate >= 0 ? 'E' : 'W';
    }
    
    return `${degrees}°${minutes}'${seconds}"${direction}`;
  };

  // Submit location coordinates
  const onSubmit = async (data) => {
    if (coordinatesValid === false) {
      toast.error('Please enter valid coordinates within India');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await LocationAPI.submitLocation(
        orgId, data.latitude, data.longitude
      );

      if (result.success) {
        toast.success('Location data saved successfully');
        
        // Store location completion flag
        localStorage.setItem('locationCompleted', 'true');
        
        // Redirect to corruption declaration page
        navigate('/main/corruption-declaration');
      } else {
        setError(result.message || 'Failed to save location data');
        toast.error(result.message || 'Failed to save location data');
      }
    } catch (error) {
      console.error('Error submitting location:', error);
      const errorMessage = 'Failed to save location data. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Container maxWidth="md">
        <Fade in timeout={1000}>
          <Paper
            elevation={12}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 4,
                textAlign: 'center',
              }}
            >
              <LocationIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
                Location Verification
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Please provide your laboratory's coordinates for verification
              </Typography>
            </Box>

            <CardContent sx={{ p: 4 }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{ mb: 3, borderRadius: 2 }}
                  icon={<ErrorIcon />}
                >
                  {error}
                </Alert>
              )}

              {coordinatesValid === true && (
                <Alert
                  severity="success"
                  sx={{ mb: 3, borderRadius: 2 }}
                  icon={<CheckCircleIcon />}
                >
                  Coordinates are valid and within India
                </Alert>
              )}

              {/* <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<MyLocationIcon />}
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      borderColor: '#764ba2',
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    },
                  }}
                >
                  {gettingLocation ? 'Getting Location...' : 'Get Current Location'}
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Or enter coordinates manually below
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                  Note: Browser location may not be 100% accurate. Verify with Google Maps if needed.
                </Typography>
              </Box> */}

              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="latitude"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Latitude"
                          placeholder="e.g., 28.6217426"
                          variant="outlined"
                          error={!!errors.latitude}
                          helperText={errors.latitude?.message || 'Enter latitude in decimal degrees'}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationIcon color="primary" />
                              </InputAdornment>
                            ),
                            endAdornment: validating && (
                              <InputAdornment position="end">
                                <Box sx={{ width: 20, height: 20 }}>
                                  <LinearProgress size={20} />
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="longitude"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Longitude"
                          placeholder="e.g., 77.0882695"
                          variant="outlined"
                          error={!!errors.longitude}
                          helperText={errors.longitude?.message || 'Enter longitude in decimal degrees'}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                          InputProps={{
                            readOnly: true,
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationIcon color="primary" />
                              </InputAdornment>
                            ),
                            endAdornment: validating && (
                              <InputAdornment position="end">
                                <Box sx={{ width: 20, height: 20 }}>
                                  <LinearProgress size={20} />
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4, textAlign: 'center'}}>
                  <Button
                  variant="outlined"
                  size="large"
                  startIcon={<MyLocationIcon />}
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    mr: 2,
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': {
                      borderColor: '#764ba2',
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    },
                  }}
                >
                  {gettingLocation ? 'Getting Location...' : 'Get Current Location'}
                </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    disabled={loading || coordinatesValid === false || validating}
                    sx={{
                      px: 6,
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      },
                    }}
                  >
                    <LoaderWrapper loading={loading} type="button">
                      {loading ? 'Saving Location...' : 'Save Location & Continue'}
                    </LoaderWrapper>
                  </Button>
                </Box>
              </form>

              <Box sx={{ mt: 4, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Instructions:
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ fontWeight: 'bold' }}>
                  The latitude and longitude to be provided for the location/address of the laboratory.
                </Typography>
                
                <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
                  1. To get coordinates for address/location of laboratory:
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • On your computer, open Google Maps ({' '}
                  <a 
                    href="https://www.google.com/maps" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'none' }}
                  >
                    https://www.google.com/maps
                  </a>
                  )
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Right-click the place or area on the map. This will open a pop-up window.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • You can find your latitude and longitude in decimal format at the top.
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Or click "Get Current Location" button above to automatically detect your coordinates
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ fontStyle: 'italic', color: '#666' }}>
                  • Example: Rohini, Delhi area coordinates are approximately: 28.7041° N, 77.1025° E
                </Typography>

                <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
                  2. Enter the coordinates in the following format:
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Use dot (.) and do not use comma (,)
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • <span style={{ color: '#4caf50', fontWeight: 'bold' }}>Correct:</span> 41.40338, 2.17403
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • <span style={{ color: '#f44336', fontWeight: 'bold' }}>Incorrect:</span> 41,40338, 2,17403
                </Typography>

                <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>
                  3. Note:
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • List your latitude coordinates before longitude coordinates
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Check that the latitude coordinate is between -90 and 90
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • Check that the longitude coordinate is between -180 and 180
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Coordinates must be within India's geographical boundaries
                </Typography>
              </Box>
            </CardContent>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default LocationPage;